import { useEffect, useState } from "react";
import { FullPageNotice } from "@/components/FullPageNotice";

/**
 * How long the app waits for the sign-in SDK before it stops saying nothing.
 *
 * Clerk resolves in well under a second on a warm cache. Five seconds is long
 * enough that nobody on a slow connection meets this screen, and short enough
 * that a browser which will never load it is not left staring at white.
 */
export const SESSION_BOOTSTRAP_TIMEOUT_MS = 5000;

/**
 * FP-170 (2026-10-07): when the app starts saying the sign-in service failed. At 5 s
 * it said so at once, and on a slow connection a user who would have been signed in
 * at 7 s had already been told the app had failed ("Anthony would report it as the
 * app didn't load", Paul). From 5 s it now says it is still checking, with a retry;
 * only after this long does it claim the service didn't answer.
 */
export const SESSION_BOOTSTRAP_FAILURE_MS = 20000;

/**
 * What every gated route renders while Clerk is still loading, and what it
 * renders when Clerk never does.
 *
 * BEN-141 QF4, and the single largest finding in the marathon baseline: forcing
 * `forbidden` blanked all eighteen signed-in routes, byte-identically
 * (`state.blank`, 0 chars of text, one shared fingerprint). The route was not
 * the variable. `useAuth().isLoaded` was: `OnboardingGate`, `RootRedirect` and
 * Clerk's own `<SignedIn>`/`<SignedOut>` all render nothing until it turns
 * true, and a 403 on Clerk's `/v1/dev_browser` means it never does. Measured
 * against the deployed dev host: a 403 on that one request leaves /dashboard at
 * 0 characters indefinitely, while a 403 on every one of the app's own
 * endpoints does not.
 *
 * That is not only a QA artefact. An ad blocker, a corporate proxy or a Clerk
 * outage produces exactly the same request failure, and produced exactly the
 * same permanent white page, with no reload prompt and nothing in the DOM to
 * read.
 *
 * Returning `null` for the first few seconds is unchanged, because that is the
 * ordinary case and a flash of chrome would be worse.
 */
export function SessionBootstrapFallback() {
  const [stage, setStage] = useState<"quiet" | "slow" | "failed">("quiet");

  useEffect(() => {
    const slow = window.setTimeout(() => setStage("slow"), SESSION_BOOTSTRAP_TIMEOUT_MS);
    const failed = window.setTimeout(() => setStage("failed"), SESSION_BOOTSTRAP_FAILURE_MS);
    return () => {
      window.clearTimeout(slow);
      window.clearTimeout(failed);
    };
  }, []);

  if (stage === "quiet") return null;

  if (stage === "slow") {
    return (
      <FullPageNotice
        title="Still checking whether you're signed in"
        pending
        actionLabel="Try again"
        onAction={() => window.location.reload()}
      >
        This is taking longer than usual, which can happen on a slow connection. The page will open
        as soon as the check finishes.
      </FullPageNotice>
    );
  }

  return (
    <FullPageNotice
      title="We could not check whether you are signed in"
      actionLabel="Try again"
      onAction={() => window.location.reload()}
    >
      The sign-in service did not answer, so this page could not load. A browser extension or a
      network that blocks it will do this. Try again, and if it keeps happening, open the app in
      another browser.
    </FullPageNotice>
  );
}
